import React, { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Loader2, Printer, Truck } from 'lucide-react';
import api, { productionAPI } from '@/lib/api';
import PrintButton from '@/components/PrintButton';
import DayCalendar from '@/pages/production/DayCalendar';

async function openPdf(url, errMsg) {
    try {
        const res = await api.get(url, { responseType: 'blob' });
        window.open(URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' })), '_blank');
    } catch (e) {
        let detail = errMsg;
        try { detail = JSON.parse(await e.response.data.text()).detail; } catch { /* ignore */ }
        toast.error(detail || errMsg);
    }
}

export default function BlView({ month, clients, initialDate }) {
    const [clientId, setClientId] = useState(clients[0]?.id || null);
    const [availMap, setAvailMap] = useState({});
    const [date, setDate] = useState(initialDate);
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const [y, m] = month.split('-').map(Number);
        const last = new Date(y, m, 0).getDate();
        productionAPI.printAvailability(`${month}-01`, `${month}-${String(last).padStart(2, '0')}`)
            .then(res => setAvailMap(res.data.days || {}))
            .catch(() => setAvailMap({}));
    }, [month]);

    const enabledDays = useMemo(
        () => Object.keys(availMap).filter(d => availMap[d]?.clients?.[clientId]).sort(),
        [availMap, clientId]
    );

    useEffect(() => {
        if (enabledDays.length && !enabledDays.includes(date)) setDate(enabledDays[0]);
        // eslint-disable-next-line
    }, [enabledDays]);

    useEffect(() => {
        if (!clientId || !date) { setData(null); setLoading(false); return; }
        setLoading(true);
        productionAPI.blData(clientId, date)
            .then(res => setData(res.data))
            .catch(() => setData(null))
            .finally(() => setLoading(false));
    }, [clientId, date]);

    const client = clients.find(c => c.id === clientId);
    const weekday = date ? ['DIMANCHE', 'LUNDI', 'MARDI', 'MERCREDI', 'JEUDI', 'VENDREDI', 'SAMEDI'][new Date(date + 'T12:00:00').getDay()] : '';
    const fmt = (v) => (v === Math.trunc(v) ? Math.trunc(v) : v);
    const eur = (v) => v == null ? '—' : v.toLocaleString('fr-FR', { style: 'currency', currency: 'EUR' });

    const calendar = <DayCalendar month={month} selected={date} onSelect={setDate} enabledDays={enabledDays} testPrefix="bl-day" />;

    return (
        <div className="space-y-3" data-testid="bl-view">
            {/* Raccourcis clients */}
            <div className="flex items-center gap-2 flex-wrap">
                <span className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Clients :</span>
                {clients.map(c => (
                    <button key={c.id} onClick={() => setClientId(c.id)}
                        className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-colors ${clientId === c.id ? 'bg-slate-900 text-white' : 'bg-white border text-slate-600 hover:text-slate-900'}`}
                        data-testid={`bl-client-${c.code}`}>
                        {c.code}
                    </button>
                ))}
            </div>

            <Card>
                <CardContent className="p-4">
                    <div className="flex items-start gap-6 flex-wrap">
                        <div className="min-w-0 flex-1">
                            <h2 className="font-bold text-slate-900 mb-3 flex items-center gap-2">
                                <Truck className="w-4 h-4 text-teal-700" />
                                Bon de Livraison — {client?.code || ''} — <span className="text-teal-700 font-extrabold" data-testid="bl-weekday">{weekday}</span> {date ? date.split('-').reverse().join('/') : ''}
                            </h2>
                            {loading ? (
                                <div className="py-12 text-center text-slate-400"><Loader2 className="w-6 h-6 animate-spin mx-auto" /></div>
                            ) : !data ? (
                                <div className="py-14 text-center text-slate-400" data-testid="bl-empty">
                                    Aucune quantité pour {client?.code || 'ce client'} le {date ? date.split('-').reverse().join('/') : '—'}.
                                </div>
                            ) : (
                                <div className="max-w-md" data-testid="bl-preview">
                                    <div className="text-xs text-slate-500 mb-2 space-y-0.5">
                                        <div>BL N° : <strong className="text-slate-800" data-testid="bl-number">{data.bl_number ?? 'attribué à l\'impression'}</strong></div>
                                        <div>Point de vente : <strong className="text-slate-800">{data.point_de_vente}</strong></div>
                                    </div>
                                    <table className="text-xs border-collapse w-full">
                                        <thead><tr>
                                            <th className="border bg-slate-100 px-2 py-1 text-left">DÉSIGNATION</th>
                                            <th className="border bg-slate-100 px-2 py-1 w-16">Qté</th>
                                            {data.with_prices && <th className="border bg-slate-100 px-2 py-1 w-20 text-right">Tarif HT</th>}
                                            {data.with_prices && <th className="border bg-slate-100 px-2 py-1 w-20 text-right">Total HT</th>}
                                        </tr></thead>
                                        <tbody>
                                            {data.lines.map((l, i) => (
                                                <tr key={i} className="hover:bg-teal-50/40">
                                                    <td className="border px-2 py-0.5">{l.name}</td>
                                                    <td className="border px-2 py-0.5 text-center">{fmt(l.qty)}</td>
                                                    {data.with_prices && <td className="border px-2 py-0.5 text-right">{eur(l.pu_ht)}</td>}
                                                    {data.with_prices && <td className="border px-2 py-0.5 text-right">{eur(l.total_ht)}</td>}
                                                </tr>
                                            ))}
                                            <tr className="font-bold bg-slate-100">
                                                <td className="border px-2 py-1">TOTAL</td>
                                                <td className="border px-2 py-1 text-center" data-testid="bl-total">{fmt(data.total)}</td>
                                                {data.with_prices && <td className="border px-2 py-1" />}
                                                {data.with_prices && <td className="border px-2 py-1 text-right" data-testid="bl-total-ht">{eur(data.total_ht)}</td>}
                                            </tr>
                                            {data.with_prices && (
                                                <tr>
                                                    <td className="border px-2 py-0.5 text-slate-500" colSpan={3}>TVA {String(data.tva_rate).replace('.', ',')} %</td>
                                                    <td className="border px-2 py-0.5 text-right" data-testid="bl-tva">{eur(data.tva)}</td>
                                                </tr>
                                            )}
                                            {data.with_prices && (
                                                <tr className="font-bold bg-teal-50">
                                                    <td className="border px-2 py-1" colSpan={3}>TOTAL TTC</td>
                                                    <td className="border px-2 py-1 text-right" data-testid="bl-total-ttc">{eur(data.total_ttc)}</td>
                                                </tr>
                                            )}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>
                        <div className="flex flex-col items-end gap-2 shrink-0">
                            <div className="flex gap-2">
                                <Button variant="outline" size="sm" disabled={!data}
                                    onClick={() => openPdf(productionAPI.blPdfUrl(clientId, date), 'Aucune quantité pour cette date')}
                                    data-testid="bl-print-btn">
                                    <Printer className="w-4 h-4 mr-1.5" />PDF
                                </Button>
                                <PrintButton testId="bl-direct-print-btn" disabled={!data} errMsg="Aucune quantité pour cette date"
                                    getBlob={async () => (await api.get(productionAPI.blPdfUrl(clientId, date), { responseType: 'blob' })).data} />
                            </div>
                            {calendar}
                        </div>
                    </div>
                </CardContent>
            </Card>
        </div>
    );
}
